import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import LoginPage from './pages/LoginPage';
import AdminDashboard from './pages/AdminDashboard';
import TablesPage from './pages/TablesPage';
import CategoriesPage from './pages/CategoriesPage';
import MenuPage from './pages/MenuPage';
import OrdersPageImproved from './pages/OrdersPageImproved';
import DiscountsPage from './pages/DiscountsPage';
import UpsaleDiscountsPage from './pages/UpsaleDiscountsPage';
import OnlineOrderPage from './pages/OnlineOrderPage';
import POSScreen from './pages/POSScreen';
import CustomerOrder from './pages/CustomerOrder';
import CustomerOrderSuccess from './pages/CustomerOrderSuccess';
import CustomerOrderPage from './pages/CustomerOrderPage';
import MenuManagementPage from './pages/MenuManagementPage';
import TableManagementPageComplete from './pages/TableManagementPageComplete';
import ReportsPage from './pages/ReportsPage';
import PrinterSettingsPage from './pages/PrinterSettingsPage';
import VoiceAISettingsPage from './pages/VoiceAISettingsPage';

// RBAC Pages
import RoleManagement from './pages/RoleManagement';
import StaffManagement from './pages/StaffManagement';
import AuditLogs from './pages/AuditLogs';
import StaffPINLogin from './pages/StaffPINLogin';
import PermissionGuard from './components/PermissionGuard';

// Kitchen Pages
import KitchenPanel from './pages/KitchenPanel';
import KitchenSettings from './pages/KitchenSettings';

// Master Admin Pages
import AdminLoginPage from './pages/AdminLoginPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import CreateRestaurantPage from './pages/CreateRestaurantPage';
import RestaurantPage from './pages/RestaurantPage';
import RestaurantDetailPage from './pages/RestaurantDetailPage';
import EditRestaurantPage from './pages/EditRestaurantPage';
import ActivityLogsPage from './pages/ActivityLogsPage';
import SuperAdminEmployees from './pages/SuperAdminEmployees';
import SuperAdminLoginHistory from './pages/SuperAdminLoginHistory';
import SuperAdminOrders from './pages/SuperAdminOrders';
import SuperAdminKitchenMonitor from './pages/SuperAdminKitchenMonitor';
import SuperAdminSalesReports from './pages/SuperAdminSalesReports';
import SuperAdminCommissionReports from './pages/SuperAdminCommissionReports';
import SuperAdminPerformanceReports from './pages/SuperAdminPerformanceReports';

// Password Reset Pages
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AdminForgotPasswordPage from './pages/AdminForgotPasswordPage';
import AdminResetPasswordPage from './pages/AdminResetPasswordPage';

import { Toaster } from '@/components/ui/sonner';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }
  
  return isAuthenticated ? children : <Navigate to="/login" />;
};

function AppRoutes() {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      {/* Master Admin Routes (No AuthProvider needed) */}
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route path="/admin/forgot-password" element={<AdminForgotPasswordPage />} />
      <Route path="/admin/reset-password" element={<AdminResetPasswordPage />} />
      <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
      <Route path="/admin/employees" element={<SuperAdminEmployees />} />
      <Route path="/admin/orders" element={<SuperAdminOrders />} />
      <Route path="/admin/kitchen-monitor" element={<SuperAdminKitchenMonitor />} />
      <Route path="/admin/reports/sales" element={<SuperAdminSalesReports />} />
      <Route path="/admin/reports/commission" element={<SuperAdminCommissionReports />} />
      <Route path="/admin/reports/performance" element={<SuperAdminPerformanceReports />} />
      <Route path="/admin/login-history" element={<SuperAdminLoginHistory />} />
      <Route path="/admin/restaurants" element={<RestaurantPage />} />
      <Route path="/admin/restaurants/create" element={<CreateRestaurantPage />} />
      <Route path="/admin/restaurants/:restaurantId" element={<RestaurantDetailPage />} />
      <Route path="/admin/restaurants/:restaurantId/edit" element={<EditRestaurantPage />} />
      <Route path="/admin/activity-logs" element={<ActivityLogsPage />} />
      
      {/* Restaurant Routes (Protected by AuthProvider) */}
      <Route path="/staff-login" element={<StaffPINLogin />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/dashboard" /> : <LoginPage />}
      />
      <Route
        path="/"
        element={isAuthenticated ? <Navigate to="/dashboard" /> : <Navigate to="/login" />}
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <PermissionGuard requiredPermissions={['reports.view', 'orders.view']}>
              <AdminDashboard />
            </PermissionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/tables"
        element={
          <ProtectedRoute>
            <TablesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/categories"
        element={
          <ProtectedRoute>
            <PermissionGuard requiredPermissions={['categories.view', 'menu.view']}>
              <CategoriesPage />
            </PermissionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/menu"
        element={
          <ProtectedRoute>
            <MenuPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/orders"
        element={
          <ProtectedRoute>
            <PermissionGuard requiredPermissions={['orders.view', 'orders.edit']}>
              <OrdersPageImproved />
            </PermissionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <PermissionGuard requiredPermissions={['reports.view']}>
              <ReportsPage />
            </PermissionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/discounts"
        element={
          <ProtectedRoute>
            <PermissionGuard requiredPermissions={['menu.edit', 'settings.edit']}>
              <DiscountsPage />
            </PermissionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/menu-management"
        element={
          <ProtectedRoute>
            <PermissionGuard requiredPermissions={['menu.view', 'menu.edit']}>
              <MenuManagementPage />
            </PermissionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/table-management"
        element={
          <ProtectedRoute>
            <PermissionGuard requiredPermissions={['tables.view', 'tables.edit']}>
              <TableManagementPageComplete />
            </PermissionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/printer-settings"
        element={
          <ProtectedRoute>
            <PermissionGuard requiredPermissions={['settings.view', 'settings.edit']}>
              <PrinterSettingsPage />
            </PermissionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/voice-ai-settings"
        element={
          <ProtectedRoute>
            <PermissionGuard requiredPermissions={['bot.view', 'bot.config']}>
              <VoiceAISettingsPage />
            </PermissionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/roles"
        element={
          <ProtectedRoute>
            <PermissionGuard requiredPermissions={['staff.manage']}>
              <RoleManagement />
            </PermissionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/staff"
        element={
          <ProtectedRoute>
            <PermissionGuard requiredPermissions={['staff.view', 'staff.manage']}>
              <StaffManagement />
            </PermissionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/audit-logs"
        element={
          <ProtectedRoute>
            <PermissionGuard requiredPermissions={['actionlog.view']}>
              <AuditLogs />
            </PermissionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/kitchen"
        element={
          <ProtectedRoute>
            <PermissionGuard requiredPermissions={['kitchen.view']}>
              <KitchenPanel />
            </PermissionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/kitchen-settings"
        element={
          <ProtectedRoute>
            <PermissionGuard requiredPermissions={['kitchen.settings', 'settings.edit']}>
              <KitchenSettings />
            </PermissionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/upsale-discounts"
        element={
          <ProtectedRoute>
            <PermissionGuard requiredPermissions={['menu.edit', 'settings.edit']}>
              <UpsaleDiscountsPage />
            </PermissionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/pos"
        element={
          <ProtectedRoute>
            <POSScreen />
          </ProtectedRoute>
        }
      />
      <Route path="/order/:tableId" element={<CustomerOrder />} />
      <Route path="/qr-order/:tableId" element={<CustomerOrderPage />} />
      <Route path="/order-success/:tableId/:orderId" element={<CustomerOrderSuccess />} />
      <Route path="/online/:restaurantSlug" element={<OnlineOrderPage />} />
      <Route path="/:restaurantSlug" element={<OnlineOrderPage />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <Toaster position="top-right" />
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;